package com.elan.edubridge.shared.data.datasource

import com.elan.edubridge.shared.domain.model.AcademyMembership
import com.elan.edubridge.shared.domain.model.AgeGroup
import com.elan.edubridge.shared.domain.model.AuthResult
import com.elan.edubridge.shared.domain.model.BirthDate
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.MembershipStatus
import com.elan.edubridge.shared.domain.model.ProfileUpdateResult
import com.elan.edubridge.shared.domain.model.UserAccount
import com.elan.edubridge.shared.domain.model.UserRole
import com.elan.edubridge.shared.domain.policy.PermissionPolicy

/** 데모 모드에서 Google 로그인이 착지할 계정. 데모 계정은 이메일·비밀번호 계정과 동일한 것을 쓴다. */
private const val DEMO_GOOGLE_ACCOUNT_ID = "student"

class InMemoryAccountDataSource : AccountDataSource {
    private data class Credential(
        val account: UserAccount,
        val password: String,
    )

    private val credentials = mutableListOf(
        credential("student", "student@edubridge.demo", "김학생"),
        credential("parent", "parent@edubridge.demo", "김학부모"),
        credential("teacher", "teacher@edubridge.demo", "김선생님"),
        credential("owner", "owner@edubridge.demo", "김학원장"),
        credential(
            "admin",
            "admin@edubridge.demo",
            "전체 관리자",
            globalRoles = setOf(UserRole.SUPER_ADMIN),
        ),
    )

    /**
     * 데모 계정의 학원 권한. 기본 역할은 모두 STUDENT이고 화면에 보이는 역할은
     * 이 멤버십을 거쳐 결정된다.
     */
    private val membershipsByUser = mutableMapOf(
        // 보호자 데모 계정("parent")은 여기 없다 — 보호자는 학원 멤버십을 받지 않고
        // GuardianLink로만 학생에 연결되므로 멤버십 없는 계정이 곧 보호자의 형태다.
        "teacher" to listOf(demoMembership("teacher", UserRole.TEACHER, listOf("class-math-1"))),
        "owner" to listOf(demoMembership("owner", UserRole.ACADEMY_OWNER)),
    )

    private var signedInAccount: UserAccount? = null

    override suspend fun login(email: String, password: String): AuthResult {
        val credential = credentials.firstOrNull {
            it.account.email.equals(email.trim(), ignoreCase = true) && it.password == password
        } ?: return AuthResult(errorMessage = "이메일 또는 비밀번호를 확인해 주세요.")

        signedInAccount = credential.account
        return AuthResult(account = credential.account)
    }

    override suspend fun register(
        email: String,
        password: String,
        name: String,
        birthDate: String,
        ageGroup: AgeGroup,
    ): AuthResult {
        val normalizedBirthDate = BirthDate.normalize(birthDate)
        if (email.isBlank() || name.isBlank() || normalizedBirthDate == null) {
            return AuthResult(errorMessage = "이름, 이메일, 올바른 생년월일을 입력해 주세요.")
        }
        if (password.length < 6) {
            return AuthResult(errorMessage = "비밀번호는 6자 이상이어야 합니다.")
        }
        if (credentials.any { it.account.email.equals(email, ignoreCase = true) }) {
            return AuthResult(errorMessage = "이미 가입된 이메일입니다.")
        }

        val account = UserAccount(
            id = "user-${credentials.size + 1}",
            email = email,
            name = name,
            baseRole = UserRole.STUDENT,
            birthDate = normalizedBirthDate,
            ageGroup = ageGroup,
        )
        credentials += Credential(account, password)
        signedInAccount = account
        return AuthResult(account = account)
    }

    /**
     * 데모 소스에는 검증할 Google 자격증명이 없으므로 [idToken]은 쓰지 않는다.
     * 데모 계정 흐름과 일관되게 이미 있는 학생 데모 계정으로 로그인시킨다 — 데모 모드에서도
     * 소셜 버튼이 이메일 로그인과 똑같이 세션을 만든다.
     */
    override suspend fun signInWithGoogle(
        idToken: String?,
        registrationName: String?,
        birthDate: String?,
    ): AuthResult {
        val account = credentials.firstOrNull { it.account.id == DEMO_GOOGLE_ACCOUNT_ID }?.account
            ?: return AuthResult(errorMessage = "데모 계정을 찾을 수 없습니다.")

        signedInAccount = account
        return AuthResult(account = account)
    }

    /** InMemory에는 복원할 영속 상태가 없다. 계약을 만족시키기 위한 즉시 반환이다. */
    override suspend fun awaitAuthReady() = Unit

    override suspend fun currentAccount(): UserAccount? = signedInAccount

    /** custom claim이 없으므로 갱신할 것도 없다. */
    override suspend fun forceRefreshToken() = Unit

    override suspend fun memberships(userId: String): List<AcademyMembership> =
        membershipsByUser[userId].orEmpty()

    override suspend fun logout() {
        signedInAccount = null
    }

    override suspend fun updateMyProfile(name: String): ProfileUpdateResult {
        val account = signedInAccount ?: return ProfileUpdateResult(
            failure = CommandFailure.UNAUTHENTICATED,
            errorMessage = "로그인 후 이름을 수정해 주세요.",
        )
        val updatedName = name.trim()
        if (updatedName.length !in 1..30) {
            return ProfileUpdateResult(
                failure = CommandFailure.INVALID_ARGUMENT,
                errorMessage = "이름은 1~30자로 입력해 주세요.",
            )
        }
        val updatedAccount = account.copy(name = updatedName)
        val index = credentials.indexOfFirst { it.account.id == account.id }
        if (index >= 0) {
            credentials[index] = credentials[index].copy(account = updatedAccount)
        }
        signedInAccount = updatedAccount
        return ProfileUpdateResult(name = updatedName)
    }

    private fun credential(
        id: String,
        email: String,
        name: String,
        globalRoles: Set<UserRole> = emptySet(),
    ) = Credential(
        account = UserAccount(
            id = id,
            email = email,
            name = name,
            baseRole = UserRole.STUDENT,
            globalRoles = globalRoles,
        ),
        password = "password",
    )
}

/**
 * 권한은 역할에서 파생해 멤버십에 **저장**한다 — 실서비스에서 이 계산은 Functions가 하고
 * 클라이언트는 저장된 값을 읽기만 한다(§2.2). 데모 소스도 같은 규칙을 흉내낸다.
 */
private fun demoMembership(
    userId: String,
    role: UserRole,
    assignedClassIds: List<String> = emptyList(),
) = AcademyMembership(
    academyId = DEMO_ACADEMY_ID,
    academyName = "ABC 수학학원",
    userId = userId,
    role = role,
    permission = PermissionPolicy.permissionFor(role),
    status = MembershipStatus.ACTIVE,
    assignedClassIds = assignedClassIds,
    activatedAt = "2026-03-01T09:00:00Z",
)
